const { readSharedSession } = require('../../../../GamerSky.Valorant/src/assets/js/riotSharedSession');
const { buildRiotBindRequest, readLegacyShopAccountSession, saveRiotRecordSession } = require('./riotAccountSession');
const { withTimeout } = require('./accountIdentity');
const { RIOT_DATA_REQUEST_TIMEOUT_MS } = require('./valorantRequestPolicy');

function failure(code) { return Object.assign(new Error(code), { code }); }
function codeOf(error) {
    const code = error && (error.code || error.message);
    return /^RIOT_[A-Z_]+$/.test(code || '') ? code : 'RIOT_QUICK_REQUEST_FAILED';
}
function quickBindFailureMessage(result) {
    if (result.pending.length) return '部分账号绑定结果待确认，请稍后重试';
    const credentialErrors = new Set(['RIOT_SESSION_EXPIRED', 'RIOT_SESSION_NOT_READY', 'RIOT_SESSION_INVALID']);
    if (result.failures.length && result.failures.every(item => credentialErrors.has(item.code))) {
        return '所选账号登录状态已失效，请重新登陆';
    }
    return '所选账号取数或绑定未完成，请稍后重试';
}
function validSession(session, puuid) {
    if (session && session.puuid && session.puuid !== puuid) throw failure('RIOT_ACCOUNT_MISMATCH');
    if (!session || session.puuid !== puuid || typeof session.accessToken !== 'string' || !session.accessToken.trim()
        || typeof session.entitlementsToken !== 'string' || !session.entitlementsToken.trim()) {
        throw failure('RIOT_SESSION_NOT_READY');
    }
    return session;
}
function isRenewalHardFailure(error) {
    return ['RIOT_ACCOUNT_MISMATCH', 'RIOT_CLIENT_CANCELLED', 'RIOT_LOGIN_OBSOLETE',
        'RIOT_VSHOP_STAGE_CANCELLED', 'RIOT_STORAGE_INVALID'].includes(codeOf(error));
}
function normalizeShopAccounts(response) {
    const rows = response && response.userAccountInfo && response.userAccountInfo.accountInfoList;
    if (!response || response.error || !Array.isArray(rows)) throw failure('RIOT_QUICK_LIST_FAILED');
    const seen = new Set();
    return rows.reduce((accounts, row) => {
        const puuid = row && typeof row.valorantId === 'string' ? row.valorantId.trim() : '';
        let name = row && typeof row.valorantAccount === 'string' ? row.valorantAccount.trim() : '';
        if (!name && row) {
            const riotName = typeof row.valorantName === 'string' ? row.valorantName.trim() : '';
            const tag = typeof row.valorantIdNumber === 'string' ? row.valorantIdNumber.trim().replace(/^#/, '') : '';
            if (riotName && tag) name = `${riotName}#${tag}`;
        }
        if (!/^[A-Za-z0-9-]{1,256}$/.test(puuid) || !name) return accounts;
        if (!seen.has(puuid)) { seen.add(puuid); accounts.push({ puuid, name }); }
        return accounts;
    }, []);
}

async function runQuickBindBatch(options) {
    const selected = new Set(options.selectedPuuids);
    const seen = new Set();
    const queue = options.accounts.filter(account => {
        if (!selected.has(account.puuid) || seen.has(account.puuid)) return false;
        seen.add(account.puuid);
        return true;
    });
    const result = { successes: [], failures: [], pending: [], last: null, cancelled: false };
    const progress = completed => {
        if (options.onProgress && options.isCurrent()) options.onProgress({ completed, total: queue.length });
    };
    progress(0);
    for (const account of queue) {
        if (!options.isCurrent()) { result.cancelled = true; break; }
        let active = true;
        const isCurrent = () => active && options.isCurrent();
        try {
            const outcome = await withTimeout(options.process(account, isCurrent), options.itemTimeoutMs || 180000);
            if (!isCurrent()) { result.cancelled = true; break; }
            const success = { ...outcome, account };
            result.successes.push(success);
            result.last = success;
        } catch (error) {
            if (!options.isCurrent()) { result.cancelled = true; break; }
            const code = codeOf(error);
            (code === 'RIOT_BIND_UNCONFIRMED' ? result.pending : result.failures).push({ account, code });
        } finally { active = false; }
        progress(result.successes.length + result.failures.length + result.pending.length);
    }
    return result;
}

function isUnconfirmedRiotBindingResponse(response, puuid) {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return true;
    if (response.error || (response.code !== undefined && response.code !== 0)) return false;
    if ((response.sourceAccountId && response.sourceAccountId !== puuid)
        || (response.dataSource && response.dataSource !== 'RIOT_PD')) return false;
    return !response.bindInfoId || !response.valorantUserName || !response.riotStats
        || !response.riotStats.view || typeof response.riotStats.view !== 'object' || Array.isArray(response.riotStats.view);
}

function validResult(response, puuid) {
    if (!response || response.error || (response.code !== undefined && response.code !== 0)) throw failure(typeof (response && response.error) === 'string'
        && /^RIOT_[A-Z_]+$/.test(response.error) ? response.error : 'RIOT_QUICK_REQUEST_FAILED');
    if (response.sourceAccountId !== puuid || response.dataSource !== 'RIOT_PD') throw failure('RIOT_ACCOUNT_MISMATCH');
    if (!response.bindInfoId || !response.valorantUserName || !response.riotStats || !response.riotStats.view
        || typeof response.riotStats.view !== 'object' || Array.isArray(response.riotStats.view)) {
        throw failure('RIOT_BIND_FAILED');
    }
    return response;
}

function createQuickBindProcessor(options) {
    return async (account, itemCurrent) => {
        const isCurrent = () => options.isCurrent() && itemCurrent();
        const check = () => { if (!isCurrent()) throw failure('RIOT_CLIENT_CANCELLED'); };
        const bindingFor = rows => rows.find(row => row.dataSource === 'RIOT_PD' && row.sourceAccountId === account.puuid);
        const readBinding = async binding => {
            check();
            const response = await withTimeout(options.api.getRiotStats({
                gamerskyUserId: options.userId, bindInfoId: binding.bindInfoId, puuid: account.puuid,
                queue: 'all', season: 'all', update: false, autoRefresh: false, clientFetch: true,
                queryByBinding: true,
            }), 15000);
            check();
            return validResult(response, account.puuid);
        };
        check();
        const existing = bindingFor(options.bindings);
        if (existing) return { response: await readBinding(existing), reused: true };
        const traceId = options.traceId ? options.traceId() : undefined;
        const startedAt = Date.now();
        const stage = (name, detail) => {
            if (options.onStage && isCurrent()) options.onStage(traceId, name, { elapsedMs: Date.now() - startedAt, ...detail });
        };
        stage('FLOW_STARTED');
        let submitted = false;
        let responseReceived = false;
        let captureActive = true;
        let renewalPending = false;
        const captureCurrent = () => captureActive && isCurrent();
        const checkCapture = () => { if (!captureCurrent()) throw failure('RIOT_CLIENT_CANCELLED'); };
        let session = null;
        try {
            session = readSharedSession(options.storage, options.userId, account.puuid);
            if (!session) {
                // The authenticated shop list proves this PUUID belongs to the
                // current GamerSky user. Migrate only that account's old local
                // token object; never scan or import unrelated storage keys.
                session = readLegacyShopAccountSession(options.storage, account.puuid);
                if (session) {
                    check();
                    const published = options.publish(session, isCurrent);
                    if (published && published.puuid === account.puuid) session = published;
                }
            }
            validSession(session, account.puuid);
            const response = await withTimeout((async () => {
                checkCapture();
                let renewed = null;
                renewalPending = true;
                stage('SILENT_RENEW_STARTED');
                try {
                    // Both existing renewal adapters read the record account store.
                    saveRiotRecordSession(options.storage, session);
                    const candidate = await options.renew(session, captureCurrent);
                    checkCapture();
                    renewed = validSession(candidate, account.puuid);
                } catch (error) {
                    checkCapture();
                    renewalPending = false;
                    stage('SILENT_RENEW_FAILED', { success: false, failType: codeOf(error) });
                    if (isRenewalHardFailure(error) && codeOf(error) !== 'RIOT_ACCOUNT_MISMATCH') throw error;
                    // A failed renewal does not prove the original tokens expired.
                    // The native cookie jar may belong to another selected account;
                    // discard that result and validate the original tokens in capture.
                }
                if (renewed) {
                    session = renewed;
                    renewalPending = false;
                    stage('SILENT_RENEW_SUCCEEDED', { success: true });
                    try { session = validSession(options.publish(session, captureCurrent), account.puuid); }
                    catch (error) { checkCapture(); if (isRenewalHardFailure(error)) throw error; }
                }
                checkCapture();
                const params = buildRiotBindRequest(options.userId, session, traceId);
                // Only final batch activation may change the default account.
                params.deferActivation = true;
                const payload = await options.prepare(params, captureCurrent);
                checkCapture();
                stage('BIND_REQUEST_STARTED');
                submitted = !payload.clientError;
                const response = await options.api.bindRiotAccount(payload);
                checkCapture();
                responseReceived = !isUnconfirmedRiotBindingResponse(response, account.puuid);
                if (!responseReceived) throw failure('RIOT_BIND_UNCONFIRMED');
                return validResult(response, account.puuid);
            })(), RIOT_DATA_REQUEST_TIMEOUT_MS);
            captureActive = false;
            check();
            // Persistence is already confirmed. Local storage failure must not
            // turn a saved binding into a reported binding failure.
            try {
                saveRiotRecordSession(options.storage, session);
                options.publish(session, isCurrent);
                stage('SESSION_SAVED');
            } catch (_) { check(); }
            stage('FLOW_FINISHED', { success: true });
            return { response, reused: false, session };
        } catch (error) {
            captureActive = false;
            check();
            if (renewalPending) {
                renewalPending = false;
                stage('SILENT_RENEW_FAILED', { success: false, failType: codeOf(error) });
            }
            // Only an unconfirmed write is reconciled. A business failure is never
            // replaced by an older cached success, and no second bind is submitted.
            if (submitted && !responseReceived) {
                for (let attempt = 0; attempt < 2; attempt++) {
                    if (attempt) await new Promise(resolve => setTimeout(resolve,
                        options.reconciliationDelayMs === undefined ? 2000 : options.reconciliationDelayMs));
                    check();
                    try {
                        const list = await withTimeout(options.api.getBindAccountList({ gamerskyUserId: options.userId }), 5000);
                        check();
                        const found = list && !list.error && (list.code === undefined || list.code === 0)
                            && Array.isArray(list.bindAccountList) && bindingFor(list.bindAccountList);
                        if (found) {
                            const response = await readBinding(found);
                            stage('FLOW_FINISHED', { success: true });
                            return { response, reused: false, session };
                        }
                    } catch (_) { check(); }
                }
            }
            const failType = submitted && !responseReceived ? 'RIOT_BIND_UNCONFIRMED' : codeOf(error);
            stage('FLOW_FINISHED', { success: false, failType });
            throw failure(failType);
        } finally { captureActive = false; }
    };
}

module.exports = { normalizeShopAccounts, runQuickBindBatch, createQuickBindProcessor, quickBindFailureMessage, isUnconfirmedRiotBindingResponse };
